import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { requestOpenAddCoinModal } from 'utils/onboardingWalkthrough';

const ASSETS_STEPS = [
  {
    Icon: AddCircleOutlineIcon,
    title: 'Add your first coin',
    detail: 'Tap + Add at the bottom, search for a coin, then enter quantity and buy price.',
  },
  {
    Icon: FilterListIcon,
    title: 'Filter by category',
    detail: 'Use All, BlueChip, DeFi, and other pills on the left to organize your holdings.',
  },
  {
    Icon: TouchAppIcon,
    title: 'Open coin details',
    detail: 'Select a coin from the list to view Fibonacci charts, sell targets, and profit metrics.',
  },
  {
    Icon: TrendingUpIcon,
    title: 'Track your XGain',
    detail: 'Dashboard charts, allocation tables, and category views update live as prices move.',
  },
];

const CATEGORY_STEPS = [
  {
    Icon: AddCircleOutlineIcon,
    title: 'Add coins to this category',
    detail: 'Use + Add and pick a category pill when saving — coins land in the matching view.',
  },
  {
    Icon: TrendingUpIcon,
    title: 'See live metrics here',
    detail: 'Price, quantity, invested, worth, P&L, XGain, and deltas appear row by row.',
  },
  {
    Icon: FilterListIcon,
    title: 'Filter by exchange',
    detail: 'Use the exchange icons above the table to focus on coins held on a specific platform.',
  },
];

function StepRow({ Icon, title, detail, index }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(149, 242, 5, 0.12)',
          border: '1px solid rgba(149, 242, 5, 0.3)',
          color: '#95F205',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ minWidth: 0, pt: 0.25 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
          <Icon sx={{ fontSize: 16, color: '#98C9F6' }} />
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Stack>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 1.5 }}>
          {detail}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function EmptyPortfolioGuide({
  variant = 'assets',
  onAddClick,
  compact = false,
}) {
  const steps = variant === 'category' ? CATEGORY_STEPS : ASSETS_STEPS;
  const title = variant === 'category'
    ? 'No coins in this view yet'
    : 'This is your Assets page';
  const subtitle = variant === 'category'
    ? 'Add coins and assign a category — this table fills with live prices, P&L, and XGain metrics.'
    : 'Build your portfolio here. Once you add coins, this page becomes your command center for every holding.';

  const handleAdd = () => {
    if (onAddClick) {
      onAddClick();
      return;
    }
    requestOpenAddCoinModal();
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: compact ? 'auto' : '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        py: compact ? 3 : { xs: 4, md: 6 },
        pb: compact ? 10 : { xs: 12, md: 6 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 4,
          p: { xs: 2.5, sm: 3 },
          bgcolor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(149, 242, 5, 0.18)',
          backgroundImage: `
            radial-gradient(ellipse 70% 45% at 50% -5%, rgba(149, 242, 5, 0.1), transparent 55%),
            linear-gradient(165deg, rgba(42, 51, 72, 0.55) 0%, rgba(26, 29, 40, 0.35) 100%)
          `,
        }}
      >
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 800,
            fontSize: { xs: '1.25rem', sm: '1.4rem' },
            letterSpacing: '-0.02em',
            mb: 1,
            textAlign: compact ? 'left' : 'center',
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.68)',
            fontSize: 14,
            lineHeight: 1.55,
            mb: 2.5,
            textAlign: compact ? 'left' : 'center',
          }}
        >
          {subtitle}
        </Typography>

        <Stack spacing={1.75} sx={{ mb: 3 }}>
          {steps.map((step, index) => (
            <StepRow key={step.title} {...step} index={index} />
          ))}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: compact ? 'flex-start' : 'center' }}>
          <Button
            onClick={handleAdd}
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              color: '#95F205',
              borderColor: 'rgba(149, 242, 5, 0.55)',
              borderRadius: '999px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': {
                borderColor: '#95F205',
                bgcolor: 'rgba(149, 242, 5, 0.08)',
              },
            }}
          >
            Add your first coin
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
